import { useMemo, type ReactNode } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '@planitgeo/treeplotter-ui-library';
import type { UseLocalesHook } from './createUseLocales';
import type { LangOption } from './types';

type Props = {
  children: ReactNode;
};

/**
 * Build a `ThemeLocalization` component bound to an app's `useLocales`.
 *
 * The component layers the current language's MUI locale object on top of
 * the outer ui-library theme so that MUI subcomponents (date pickers,
 * data grid, etc.) render in the user's language. Mount it inside the
 * ui-library `ThemeProvider`.
 */
export function createThemeLocalization<T extends LangOption>(
  useLocales: UseLocalesHook<T>
): (props: Props) => JSX.Element {
  return function ThemeLocalization({ children }: Props) {
    const outerTheme = useTheme();
    const { currentLang } = useLocales();
    // `createTheme` walks the entire MUI theme palette and merges the locale
    // object into it — non-trivial work to repeat on every parent re-render.
    // The result only depends on the outer theme and the active language's
    // MUI locale, so memoize on those two inputs.
    const theme = useMemo(
      () => createTheme(outerTheme, currentLang.systemValue as object),
      [outerTheme, currentLang.systemValue]
    );
    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
  };
}
